"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "This is not a GL statement",
    rule: "Accountant → Account Ledger is a master of named ledgers (Ledger Name, GST, PAN). Chart of Accounts row click is the account statement. Do not mix them",
  },
  {
    topic: "Subscription gate",
    rule: "Creating is limited by the Account Ledger plan feature. Editing and deleting are not separately gated that way",
  },
  {
    topic: "Account Ledger number",
    rule: "Auto-generated unless you turn that off under Configure Account Ledger Number Preferences on the form. Default prefix ACCPA. Locked after save when auto-generate is on",
  },
  {
    topic: "No CoA link, no opening balance",
    rule: "There is no Chart of Accounts picker, type, lock, currency, branch, or opening balance on this form",
  },
  {
    topic: "Where it is picked",
    rule: "Expenses: Customer/Vendor/Ledger Name (customers plus these masters). Account Payment Received / Made: Customer/Vendor Name is this dropdown only. Manual Journal Contact is Customers/Vendors of the branch — not this master",
  },
  {
    topic: "Delete",
    rule: "Blocked if used on Expenses or Manual Journals: Cannot delete: Business Account Ledger is used in one or more transactions",
  },
  {
    topic: "No detail, no export",
    rule: "List and Add/Edit only. No transaction tab, no TOOLS export, no lock",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating an account ledger" },
      { heading: "2. Viewing and searching the list" },
      { heading: "3. Where this ledger is picked" },
      { heading: "4. Editing and deleting" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function AccountLedgerPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Account Ledger
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone who needs a named ledger (salary, a non-vendor
            payee) on Expenses — not the Chart of Accounts statement
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            <B>Account Ledger</B> on the Accountant menu is a master of
            people/ledgers: name, GST, PAN, address. Prefix{" "}
            <B>ACCPA</B>. It does <B>not</B> show invoices or running
            balances. To see what posted to Sales or Rent, open{" "}
            <B>Chart of Accounts</B> and click that account.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating an account ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>Add</B> → Add Account Ledger. <B>Save</B> / <B>Cancel</B>.
          </p>
          <Definitions
            items={[
              {
                term: "Ledger Master Number",
                desc: (
                  <>
                    Required. Auto from{" "}
                    <B>Configure Account Ledger Number Preferences</B> on
                    the gear. Default prefix <B>ACCPA</B>. Disabled when
                    auto-generate is on or on edit.
                  </>
                ),
              },
              {
                term: "Ledger Name",
                desc: <>Required. Stored as person name.</>,
              },
              {
                term: "GST Number, PAN, Phone No., Email, Address",
                desc: (
                  <>
                    Optional. GSTIN pattern is validated if you fill GST
                    Number. Country defaults to India; State and City
                    optional.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            No Chart of Accounts account, no opening balance, no
            attachments, no lock.
          </Callout>
          <Screenshot caption="Screenshot: Add Account Ledger with Ledger Master Number, Ledger Name, GST Number, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Viewing and searching the list
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title Account Ledger with a count. Search then Apply Filters.
            Columns: Account Ledger#, Ledger Name, GST, Added By, Updated
            By, Action (Edit / Delete). No export, no status tabs, no date
            range. Row does not open a statement.
          </p>
          <Callout type="note">
            Backend search matches person name, PAN, phone, email — not
            GST# or ledger# specifically.
          </Callout>
          <Screenshot caption="Screenshot: Account Ledger list with Add and search." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Where this ledger is picked
          </h3>
          <Definitions
            items={[
              {
                term: "Expenses",
                desc: (
                  <>
                    Field <B>Customer/Vendor/Ledger Name</B> merges
                    customers with these masters.
                  </>
                ),
              },
              {
                term: "Account Payment Received / Made",
                desc: (
                  <>
                    Customer/Vendor Name on those accountant payment
                    screens is this dropdown only (not Customer Master /
                    Vendor).
                  </>
                ),
              },
              {
                term: "Manual Journals",
                desc: (
                  <>
                    Contact on a journal line is a Customer or Vendor of
                    the branch. This master is not on that picker. Delete
                    is still blocked if this ledger&apos;s id appears on a
                    journal line.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Expense form Customer/Vendor/Ledger Name picking an Account Ledger." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing and deleting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Edit from the list. Number stays locked. Delete is blocked when
            the ledger is used on Expenses or Manual Journals.
          </p>
          <Screenshot caption="Screenshot: Delete Account Ledger blocked because it is used on an expense." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/accountant/chart-of-accounts"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Chart of Accounts <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Click a CoA row for Date, Debit, Credit, running totals
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/expenses"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Expenses <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Customer/Vendor/Ledger Name can pick this master
            </p>
          </Link>
          <Link
            href="/documentation/books/accountant/manual-journals"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Manual Journals <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Contact there is Customer / Vendor — not this master
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
